// src/utils/routes.tsx - OPTIMIZED PRODUCTION VERSION (NO CHANGES NEEDED)
import { Suspense, lazy, useEffect } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import App from '../App';
import { ProvidersWrapper } from '../components/providers/ProvidersWrapper';
import LoadingSpinner from '../components/shared/LoadingSpinner';
import ProtectedRoute from '../components/auth/ProtectedRoute';
import OAuthCallback from '../pages/Auth/OAuthCallback';

// Direct import for critical pages that need to load fast
import BookingConfirmation from '../pages/BookingConfirmation/BookingConfirmation';

// Optimized lazy loading with preload support
const lazyWithPreload = (factory: () => Promise<any>) => {
  const Component = lazy(factory);
  (Component as any).preload = factory;
  return Component;
};

// Preload dashboard components for faster navigation
export const preloadCustomerDashboard = () => {
  import('../pages/Dashboard/CustomerDashboard');
  import('../pages/Dashboard/CustomerOrders');
  import('../pages/Dashboard/CustomerPayments');
  import('../pages/Dashboard/CustomerProfile');
  import('../pages/Dashboard/CustomerHistory');
  import('../pages/Dashboard/CustomerSafety');
  import('../pages/Dashboard/CustomerQuickOffers');
  import('../pages/Dashboard/FavoritesPage');
};

export const preloadRiderDashboard = () => {
  import('../pages/Dashboard/RiderDashboard');
  import('../pages/Dashboard/RiderOrders');
  import('../pages/Dashboard/RiderEarnings');
  import('../pages/Dashboard/RiderProfile');
  import('../pages/Dashboard/RiderHistory');
  import('../pages/Dashboard/RiderSafety');
  import('../pages/Dashboard/RiderQuickOffers');
  import('../pages/Dashboard/RiderListings');
};

// Lazy load pages for better performance
const HomePage = lazy(() => import('../pages/HomePage'));
const Login = lazy(() => import('../pages/Auth/Login'));
const Signup = lazy(() => import('../pages/Auth/Signup'));
const ChooseRole = lazy(() => import('../pages/Auth/ChooseRole'));
const VerifyEmail = lazy(() => import('../pages/Auth/VerifyEmail'));
const Marketplace = lazy(() => import('../pages/Marketplace/Marketplace'));
const CreateListing = lazy(() => import('../pages/Marketplace/CreateListing'));
const RiderProfilePage = lazy(() => import('../pages/Marketplace/RiderProfilePage'));
const CustomerProfilePage = lazy(() => import('../pages/Marketplace/CustomerProfilePage'));
const NotificationsPage = lazy(() => import('../pages/NotificationsPage'));
const PaymentCallback = lazy(() => import('../pages/PaymentCallback'));

// Order pages
const OrderPage = lazy(() => import('../pages/Orders/OrderPage'));
const PaystackPayment = lazy(() => import('../pages/Orders/PaystackPayment'));
const P2PChat = lazy(() => import('../pages/Orders/P2PChat'));

// Customer dashboard pages
const CustomerDashboard = lazy(() => import('../pages/Dashboard/CustomerDashboard'));
const CustomerOrders = lazy(() => import('../pages/Dashboard/CustomerOrders'));
const CustomerPayments = lazy(() => import('../pages/Dashboard/CustomerPayments'));
const CustomerProfile = lazy(() => import('../pages/Dashboard/CustomerProfile'));
const CustomerHistory = lazy(() => import('../pages/Dashboard/CustomerHistory'));
const CustomerSafety = lazy(() => import('../pages/Dashboard/CustomerSafety'));
const CustomerQuickOffers = lazy(() => import('../pages/Dashboard/CustomerQuickOffers'));
const FavoritesPage = lazy(() => import('../pages/Dashboard/FavoritesPage'));

// Rider dashboard pages
const RiderDashboard = lazy(() => import('../pages/Dashboard/RiderDashboard'));
const RiderOrders = lazy(() => import('../pages/Dashboard/RiderOrders'));
const RiderEarnings = lazy(() => import('../pages/Dashboard/RiderEarnings'));
const RiderProfile = lazy(() => import('../pages/Dashboard/RiderProfile'));
const RiderHistory = lazy(() => import('../pages/Dashboard/RiderHistory'));
const RiderSafety = lazy(() => import('../pages/Dashboard/RiderSafety'));
const RiderQuickOffers = lazy(() => import('../pages/Dashboard/RiderQuickOffers'));
const RiderListings = lazy(() => import('../pages/Dashboard/RiderListings'));

// Admin pages
import AdminLayout from '../pages/Admin/AdminLayout';
import AdminDashboard from '../pages/Admin/AdminDashboard';
import AdminUsers from '../pages/Admin/AdminUsers';
import AdminRiders from '../pages/Admin/AdminRiders';
import AdminOrders from '../pages/Admin/AdminOrders';
import AdminPayments from '../pages/Admin/AdminPayments';
import AdminTickets from '../pages/Admin/AdminTickets';
import AdminSettings from '../pages/Admin/AdminSettings';
import AdminProfile from '../pages/Admin/AdminProfile';

// Support pages
import SupportLayout from '../pages/Support/SupportLayout';
import SupportDashboard from '../pages/Support/SupportDashboard';
import SupportOrders from '../pages/Support/SupportOrders';
import SupportTickets from '../pages/Support/SupportTickets';
import SupportProfile from '../pages/Support/SupportProfile';

// Forgot/Reset password pages
import ForgotPassword from '../pages/Auth/ForgotPassword';
import ResetPassword from '../pages/Auth/ResetPassword';

// Determine basename for router based on environment
const getBasename = () => {
  // Production on custom domain
  if (window.location.hostname === 'carryngo.ng' || 
      window.location.hostname === 'www.carryngo.ng') {
    return '/';
  }
  // Render.com preview deployment
  if (window.location.hostname.includes('onrender.com')) {
    return '/';
  }
  // Local development
  return '/';
};

// Optimized loading fallback component with skeleton
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <LoadingSpinner size="lg" />
  </div>
);

// Faster loading for authenticated routes
const AuthenticatedLoader = ({ children }: { children: React.ReactNode }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

export const router = createBrowserRouter(
  [
    {
      path: '/',
      element: (
        <ProvidersWrapper>
          <App />
        </ProvidersWrapper>
      ),
      children: [
        // Public routes
        {
          index: true,
          element: (
            <Suspense fallback={<PageLoader />}>
              <HomePage />
            </Suspense>
          ),
        },
        {
          path: 'login',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Login />
            </Suspense>
          ),
        },
        {
          path: '/auth/callback',
          element: (
            <Suspense fallback={<LoadingSpinner />}>
              <OAuthCallback />
            </Suspense>
          ),
        },
        {
          path: 'signup',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Signup />
            </Suspense>
          ),
        },
        {
          path: 'choose-role',
          element: (
            <Suspense fallback={<PageLoader />}>
              <ChooseRole />
            </Suspense>
          ),
        },
        {
          path: 'verify-email/:token',
          element: (
            <Suspense fallback={<PageLoader />}>
              <VerifyEmail />
            </Suspense>
          ),
        },
        {
          path: 'marketplace',
          element: (
            <Suspense fallback={<PageLoader />}>
              <Marketplace />
            </Suspense>
          ),
        },
        {
          path: 'rider/:riderId',
          element: (
            <Suspense fallback={<PageLoader />}>
              <RiderProfilePage />
            </Suspense>
          ),
        },
        {
          path: 'customer/:customerId',
          element: (
            <Suspense fallback={<PageLoader />}>
              <CustomerProfilePage />
            </Suspense>
          ),
        },
        {
          path: 'booking-confirmation',
          element: <BookingConfirmation />,
        },
        {
          path: 'notifications',
          element: (
            <ProtectedRoute>
              <AuthenticatedLoader>
                <NotificationsPage />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'payment/callback',
          element: (
            <Suspense fallback={<PageLoader />}>
              <PaymentCallback />
            </Suspense>
          ),
        },

        // Password management
        {
          path: 'forgot-password',
          element: <ForgotPassword />,
        },
        {
          path: 'reset-password/:token',
          element: <ResetPassword />,
        },

        // ============ CUSTOMER DASHBOARD ============
        {
          path: 'dashboard/customer',
          element: (
            <ProtectedRoute allowedRoles={['customer']}>
              <AuthenticatedLoader>
                <CustomerDashboard />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'customer/orders',
          element: (
            <ProtectedRoute allowedRoles={['customer']}>
              <AuthenticatedLoader>
                <CustomerOrders />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'customer/payments',
          element: (
            <ProtectedRoute allowedRoles={['customer']}>
              <AuthenticatedLoader>
                <CustomerPayments />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'customer/profile',
          element: (
            <ProtectedRoute allowedRoles={['customer']}>
              <AuthenticatedLoader>
                <CustomerProfile />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'customer/history',
          element: (
            <ProtectedRoute allowedRoles={['customer']}>
              <AuthenticatedLoader>
                <CustomerHistory />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'customer/safety',
          element: (
            <ProtectedRoute allowedRoles={['customer']}>
              <AuthenticatedLoader>
                <CustomerSafety />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'customer/quick-offers',
          element: (
            <ProtectedRoute allowedRoles={['customer']}>
              <AuthenticatedLoader>
                <CustomerQuickOffers />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'customer/favorites',
          element: (
            <ProtectedRoute allowedRoles={['customer']}>
              <AuthenticatedLoader>
                <FavoritesPage />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },

        // ============ RIDER DASHBOARD ============
        {
          path: 'dashboard/rider',
          element: (
            <ProtectedRoute allowedRoles={['rider']}>
              <AuthenticatedLoader>
                <RiderDashboard />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'rider/orders',
          element: (
            <ProtectedRoute allowedRoles={['rider']}>
              <AuthenticatedLoader>
                <RiderOrders />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'rider/earnings',
          element: (
            <ProtectedRoute allowedRoles={['rider']}>
              <AuthenticatedLoader>
                <RiderEarnings />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'rider/profile',
          element: (
            <ProtectedRoute allowedRoles={['rider']}>
              <AuthenticatedLoader>
                <RiderProfile />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'rider/history',
          element: (
            <ProtectedRoute allowedRoles={['rider']}>
              <AuthenticatedLoader>
                <RiderHistory />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'rider/safety',
          element: (
            <ProtectedRoute allowedRoles={['rider']}>
              <AuthenticatedLoader>
                <RiderSafety />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'rider/quick-offers',
          element: (
            <ProtectedRoute allowedRoles={['rider']}>
              <AuthenticatedLoader>
                <RiderQuickOffers />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'rider/listings',
          element: (
            <ProtectedRoute allowedRoles={['rider']}>
              <AuthenticatedLoader>
                <RiderListings />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },

        // ============ MARKETPLACE & LISTINGS ============
        {
          path: 'marketplace/create',
          element: (
            <ProtectedRoute allowedRoles={['rider']}>
              <AuthenticatedLoader>
                <CreateListing />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },

        // ============ ORDER ROUTES ============
        {
          path: 'order/:orderId',
          element: (
            <ProtectedRoute>
              <AuthenticatedLoader>
                <OrderPage />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'order/:orderId/chat',
          element: (
            <ProtectedRoute>
              <AuthenticatedLoader>
                <P2PChat />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },
        {
          path: 'payment/:orderId',
          element: (
            <ProtectedRoute>
              <AuthenticatedLoader>
                <PaystackPayment />
              </AuthenticatedLoader>
            </ProtectedRoute>
          ),
        },

        // ============ ADMIN ROUTES ============
        {
          path: 'admin',
          element: (
            <ProtectedRoute allowedRoles={['admin']}>
              <AdminLayout />
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: <AdminDashboard /> },
            { path: 'users', element: <AdminUsers /> },
            { path: 'riders', element: <AdminRiders /> },
            { path: 'orders', element: <AdminOrders /> },
            { path: 'payments', element: <AdminPayments /> },
            { path: 'tickets', element: <AdminTickets /> },
            { path: 'settings', element: <AdminSettings /> },
            { path: 'profile', element: <AdminProfile /> },
          ],
        },

        // ============ SUPPORT ROUTES ============
        {
          path: 'support',
          element: (
            <ProtectedRoute allowedRoles={['support']}>
              <SupportLayout />
            </ProtectedRoute>
          ),
          children: [
            { index: true, element: <SupportDashboard /> },
            { path: 'orders', element: <SupportOrders /> },
            { path: 'tickets', element: <SupportTickets /> },
            { path: 'profile', element: <SupportProfile /> },
          ],
        },

        // ============ REDIRECTS ============
        {
          path: 'customer/dashboard',
          element: <Navigate to="/dashboard/customer" replace />,
        },
        {
          path: 'rider/dashboard',
          element: <Navigate to="/dashboard/rider" replace />,
        },
        {
          path: 'create-listing',
          element: <Navigate to="/marketplace/create" replace />,
        },
        {
          path: 'orders/:orderId',
          element: <Navigate to="/order/:orderId" replace />,
        },

        // Catch-all route - MUST BE LAST
        {
          path: '*',
          element: <Navigate to="/" replace />,
        },
      ],
    },
  ],
  {
    basename: getBasename(),
  }
);

export default router;